import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, forkJoin, Observable, take, tap } from 'rxjs';
import { AgencyService } from 'src/app/services/agency.service';
import { EventService } from 'src/app/services/event.service';
import { LEPartnerService } from 'src/app/services/lepartner.service';
import { LocationService } from 'src/app/services/location.service';
import { LookupService } from 'src/app/services/lookup.service';
import { PeopleService } from 'src/app/services/people.service';

@Injectable({
  providedIn: 'root',
})
export class EventStepperDataService {
  mode: string = 'Add';
  otherStatusId: string = null;
  otherCrimeTypeId: string = null;
  eventId: string = '';
  importEventId: string = '';

  private _eventLECaseNumber: string = null;
  private eventLECaseNumber: BehaviorSubject<string> = new BehaviorSubject<string>(this._eventLECaseNumber);
  public eventLECaseNumber$: Observable<string> = this.eventLECaseNumber.asObservable();

  private _stepStatus: { step: string, state: boolean } = null;
  private stepStatus: BehaviorSubject<{ step: string, state: boolean }> = new BehaviorSubject<{ step: string, state: boolean }>(this._stepStatus);
  public stepStatus$: Observable<{ step: string, state: boolean }> = this.stepStatus.asObservable();

  private _locationAddress: ServerModels.ENESS_AddressDTO = null;
  private locationAddress: BehaviorSubject<ServerModels.ENESS_AddressDTO> = new BehaviorSubject<ServerModels.ENESS_AddressDTO>(this._locationAddress);
  public locationAddress$: Observable<ServerModels.ENESS_AddressDTO> = this.locationAddress.asObservable();

  private _addressResults: ServerModels.ENESS_AddressDTO[] = [];
  private addressResults: BehaviorSubject<ServerModels.ENESS_AddressDTO[]> = new BehaviorSubject<ServerModels.ENESS_AddressDTO[]>(this._addressResults);
  public addressResults$: Observable<ServerModels.ENESS_AddressDTO[]> = this.addressResults.asObservable();

  private _filteredAgencies: ServerModels.ENESS_REF_Agency[] = [];
  private filteredAgencies: BehaviorSubject<ServerModels.ENESS_REF_Agency[]> = new BehaviorSubject<ServerModels.ENESS_REF_Agency[]>(this._filteredAgencies);
  public filteredAgencies$: Observable<ServerModels.ENESS_REF_Agency[]> = this.filteredAgencies.asObservable();

  private _people: ServerModels.ENESS_XREF_EventsPeopleDTO[] = [];
  private people: BehaviorSubject<ServerModels.ENESS_XREF_EventsPeopleDTO[]> = new BehaviorSubject<ServerModels.ENESS_XREF_EventsPeopleDTO[]>(this._people);
  public people$: Observable<ServerModels.ENESS_XREF_EventsPeopleDTO[]> = this.people.asObservable();

  public _peopleResults: ServerModels.FindPeople[] = [];
  public peopleResults: BehaviorSubject<ServerModels.FindPeople[]> = new BehaviorSubject<ServerModels.FindPeople[]>(this._peopleResults);
  public peopleResults$: Observable<ServerModels.FindPeople[]> = this.peopleResults.asObservable();

  private _personDetails: ServerModels.ENESS_PersonDTO = null;
  private personDetails: BehaviorSubject<ServerModels.ENESS_PersonDTO> = new BehaviorSubject<ServerModels.ENESS_PersonDTO>(this._personDetails);
  public personDetails$: Observable<ServerModels.ENESS_PersonDTO> = this.personDetails.asObservable();

  private _lePartnerDetails: ServerModels.ENESS_LEPartnerDTO = null;
  private lePartnerDetails: BehaviorSubject<ServerModels.ENESS_LEPartnerDTO> = new BehaviorSubject<ServerModels.ENESS_LEPartnerDTO>(this._lePartnerDetails);
  public lePartnerDetails$: Observable<ServerModels.ENESS_LEPartnerDTO> = this.lePartnerDetails.asObservable();

  crimeTypesList$: Observable<ServerModels.ENESS_REF_CrimeTypes[]> = this.getCrimeTypes();
  crimeTypesDropdownList$: Observable<ServerModels.ENESS_REF_CrimeTypes[]> = this.lookupService.getDropdownCrimeTypes().pipe(tap((crimes: ServerModels.ENESS_REF_CrimeTypes[]) => this.otherCrimeTypeId = crimes.find(x => x.CrimeTypeName === 'Other').CrimeTypeID));
  statusesList$: Observable<ServerModels.ENESS_REF_Statuses[]> = this.lookupService.getAllStatuses().pipe(tap((status: ServerModels.ENESS_REF_Statuses[]) => this.otherStatusId = status.find((x: ServerModels.ENESS_REF_Statuses) => x.Status === 'Other').StatusID));
  timeZones$: Observable<ServerModels.ENESS_REF_TimeZones[]> = this.lookupService.getAllTimeZones();

  constructor(
    private messageService: MessageService,
    private lookupService: LookupService,
    private eventService: EventService,
    private agencyService: AgencyService,
    private locationService: LocationService,
    private peopleService: PeopleService,
    private lePartnerService: LEPartnerService
  ) { }

  public getEventByStagingId(eventStagingId: string): Observable<ServerModels.ENESS_EventDTO> {
    return this.eventService.getEventByStagingId(eventStagingId);
  }

  public createOrUpdateEvent(eventIds: any): Observable<string> {
    return this.eventService.createOrUpdateEvent(eventIds);
  }

  public addOrUpdateEventDetailsStaging(eventData: ServerModels.ENESS_EventDTO): Observable<ServerModels.ENESS_EventDTO> {
    return this.eventService.addOrUpdateEventDetailsStagingAsync(eventData);
  }

  public setEventDetails(formData: ServerModels.ENESS_EventDTO): ServerModels.ENESS_EventDTO {
    let eventDto: ServerModels.ENESS_EventDTO = <ServerModels.ENESS_EventDTO>{
      EventStagingID: formData.EventStagingID,
      LECaseNumberStagingID: formData.LECaseNumberStagingID,
      EventID: formData.EventID,
      LECaseNumberID: formData.LECaseNumberID,
      AgencyID: formData.AgencyID,
      CrimeTypeID: formData.CrimeTypeID,
      OtherCrimeType: formData.OtherCrimeType,
      OtherCrimeTypeID: formData.OtherCrimeTypeID,
      Summary: formData.Summary,
      Narrative: formData.Narrative,
      Notes: formData.Notes,
      StatusID: formData.StatusID,
      OtherStatus: formData.OtherStatus,
      CreatedBy: formData.CreatedBy,
      CreatedOn: formData.CreatedOn,
      EventDateOffset: formData.EventDateOffset,
      TimeZoneID: formData.TimeZoneID,
      Deleted: formData.Deleted,
      ENESS_XREF_LECaseNumbers: formData.ENESS_XREF_LECaseNumbers,
      ENESS_XREF_EventsLEPartners: formData.ENESS_XREF_EventsLEPartners,
      ENESS_REF_Agency: formData.ENESS_REF_Agency,
      ENESS_REF_TimeZone: formData.ENESS_REF_TimeZone
    };
    return eventDto;
  }

  public setEventLECaseNumberValue(caseNumber: string) {
    this._eventLECaseNumber = caseNumber;
    this.eventLECaseNumber.next(this._eventLECaseNumber);
  }

  public stepStatusChange(state: { step: string, state: boolean }): void {
    this._stepStatus = state;
    this.stepStatus.next(this._stepStatus);
  }

  public getCrimeTypes(eventCrimeTypeId?: string): Observable<ServerModels.ENESS_REF_CrimeTypes[]> {
    if (!eventCrimeTypeId) {
      return this.lookupService.getAllCrimeTypes(eventCrimeTypeId).pipe(take(1));
    }
    else {
      return this.lookupService.getAllCrimeTypes(eventCrimeTypeId).pipe(
        take(1),
        tap((crime: ServerModels.ENESS_REF_CrimeTypes[]) => this.otherCrimeTypeId = crime.find(x => x.CrimeTypeName === 'Other').CrimeTypeID)
      );
    }
  }

  public getFilteredAgencies(stateId: string) {
    this.agencyService.getAgenciesForState(stateId).pipe(take(1)).subscribe((agencies: ServerModels.ENESS_REF_Agency[]) => {
      this._filteredAgencies = agencies;
      this.filteredAgencies.next(this._filteredAgencies);
    });
  }

  public getFilteredAgenciesValue(): ServerModels.ENESS_REF_Agency[] {
    return this.filteredAgencies.getValue();
  }

  public getLocationDetails(addressId: string) {
    forkJoin({
      leCase: this.locationService.getAddressLECases(addressId),
      people: this.locationService.getAddressPeople(addressId),
      address: this.locationService.getAddressDetailsById(addressId)
    })
      .pipe(take(1)).subscribe({
        next: (val: { leCase: ServerModels.AddressLECases[], people: ServerModels.AddressPeople[], address: ServerModels.ENESS_AddressDTO }) => {
          this._locationAddress = val.address;
          this.locationAddress.next(val.address);
          this.locationService.addressLECasesSubj.next(val.leCase);
          this.locationService.addressPeopleSubj.next(val.people);
        }
      });
  }

  public getLocationDetailsStaging(addressStagingId: string) {
    this.locationService.getAddressDetailsStagingById(addressStagingId).pipe(take(1)).subscribe((address: ServerModels.ENESS_AddressDTO) => {
      this._locationAddress = address;
      this.locationAddress.next(address);
    });
  }

  public setLocationAddress(address: ServerModels.ENESS_AddressDTO) {
    this._locationAddress = <ServerModels.ENESS_AddressDTO>address;
    this.locationAddress.next(this._locationAddress);
  }

  public findAddresses(streetAddress: string, unit: string, cityName: string, state: string, postalCode: string, postalCodeExtended: string): void {
    this._addressResults = [];
    this.addressResults.next(this._addressResults);
    this.locationService.searchAddresses(streetAddress, unit, cityName, state, postalCode, postalCodeExtended).pipe(take(1)).subscribe((address: ServerModels.ENESS_AddressDTO[]) => {
      this._addressResults = [...address];
      this.addressResults.next(this._addressResults);
    });
  }

  public setAddressResults(addresses: ServerModels.ENESS_AddressDTO[]) {
    this._addressResults = addresses;
    this.addressResults.next(this._addressResults);
  }

  public addOrUpdatePerson(personDTO: ServerModels.ENESS_PersonDTO): Observable<ServerModels.ENESS_PersonDTO> {
    return this.peopleService.addOrUpdatePersonStagingAsync(personDTO);
  }

  public addOrUpdateEventPersonStaging(eventPersonDTO: ServerModels.ENESS_XREF_EventsPeopleDTO): Observable<ServerModels.ENESS_XREF_EventsPeopleDTO> {
    return this.eventService.addOrUpdateEventPersonStaging(eventPersonDTO);
  }

  public removeEventPerson(eventPersonStagingId: string): Observable<number> {
    return this.eventService.deleteEventPeopleStaging(eventPersonStagingId);
  }

  public copyEventPeopleToStaging(people: ServerModels.ENESS_XREF_EventsPeopleDTO[]): Observable<ServerModels.ENESS_XREF_EventsPeopleDTO[]> {
    return this.eventService.copyEventPeopleToStaging(people);
  }

  public addOrUpdateEventPeopleTypesStaging(eventPerson: ServerModels.ENESS_XREF_EventsPeopleDTO): Observable<ServerModels.ENESS_XREF_EventsPeopleDTO> {
    return this.eventService.addOrUpdateEventPeopleTypesStaging(eventPerson);
  }

  public getPersonDetails(personId: string, errMsgKey: string = '') {
    this.peopleService.getPersonDetailsDTOById(personId).pipe(take(1)).subscribe((person: ServerModels.ENESS_PersonDTO) => {
      this._personDetails = person;
      this.personDetails.next(this._personDetails);
    },
      (err:any) => this.messageService.add({ key: errMsgKey, severity: 'error', detail: 'Error:', summary: err.error || err.error.ExceptionMessage || err.error.Message })
    );
  }

  public getPersonDetailsStaging(personStagingId: string, errMsgKey: string = '') {
    this.peopleService.getPersonStagingById(personStagingId).pipe(take(1)).subscribe((person: ServerModels.ENESS_PersonDTO) => {
      this._personDetails = person;
      this.personDetails.next(this._personDetails);
    },
      (err:any) => this.messageService.add({ key: errMsgKey, severity: 'error', detail: 'Error:', summary: err.error || err.error.ExceptionMessage || err.error.Message })
    );
  }

  public setPersonDetails(personDTO: ServerModels.ENESS_PersonDTO) {
    this._personDetails = personDTO;
    this.personDetails.next(this._personDetails);
  }

  public getPersonDetailsValue(): ServerModels.ENESS_PersonDTO {
    return this.personDetails.getValue();
  }

  public getPeopleValue(): ServerModels.ENESS_XREF_EventsPeopleDTO[] {
    return this.people.getValue();
  }

  public getPeopleResultsValue(): ServerModels.FindPeople[] {
    return this.peopleResults.getValue();
  }

  public getPeopleResults(filterValues: ServerModels.FilterValues[], excludePersons: ServerModels.ENESS_XREF_EventsPeopleDTO[]) {
    this._peopleResults = [];
    this.peopleResults.next(this._peopleResults);
    this.peopleService.findPeople(filterValues).pipe(take(1)).subscribe((people: ServerModels.ENESS_PersonDTO[]) => {
      this._peopleResults = [...<ServerModels.FindPeople[]>people].filter(p => !excludePersons.some(e => p.PersonID == e.PersonID));
      this.peopleResults.next(this._peopleResults);
    },
      (err: any) => this.messageService.add({ key: 'eventPersonMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage || err.error.Message }));
  }

  public setPeopleResults(people: ServerModels.FindPeople[]) {
    this._peopleResults = people;
    this.peopleResults.next(this._peopleResults);
  }

  public deleteEventAttachmentStaging(attachmentStagingId: string): Observable<number> {
    return this.eventService.deleteEventAttachmentStaging(attachmentStagingId);
  }

  public addOrUpdateEventLEPartner(partner: ServerModels.ENESS_XREF_EventsLEPartnersDTO): Observable<ServerModels.ENESS_XREF_EventsLEPartnersDTO> {
    return this.eventService.addOrUpdateEventLEPartner(partner);
  }

  public copyEventLEPartnerToStaging(partner: ServerModels.ENESS_XREF_EventsLEPartnersDTO): Observable<ServerModels.ENESS_XREF_EventsLEPartnersDTO> {
    return this.eventService.copyEventLEPartnerToStaging(partner);
  }

  public deleteEventLEPartner(partnerStagingId: string): Observable<number> {
    return this.eventService.deleteEventLEPartner(partnerStagingId);
  }

  public getLEPartnerDetails(partnerStagingId: string): Observable<ServerModels.ENESS_LEPartnerDTO> {
    return this.lePartnerService.getLEPartnerStagingById(partnerStagingId);
  }

  public getLEPartnerDetailsValue(): ServerModels.ENESS_LEPartnerDTO {
    return this.lePartnerDetails.getValue();
  }

  public setLEPartnerDetails(lePartner: ServerModels.ENESS_LEPartnerDTO): void {
    this._lePartnerDetails = lePartner;
    this.lePartnerDetails.next(this._lePartnerDetails);
  }

  public addOrUpdateAddress(addressDto: ServerModels.ENESS_AddressDTO): Observable<string> {
    return this.locationService.addOrUpdateLocation(addressDto);
  }

  public addOrUpdateAddressStaging(addressDto: ServerModels.ENESS_AddressDTO): Observable<ServerModels.ENESS_AddressDTO> {
    return this.locationService.addOrUpdateAddressStaging(addressDto);
  }

  public addOrUpdateEventLocationStaging(eventLocationDTO: ServerModels.ENESS_XREF_EventsLocationsDTO): Observable<ServerModels.ENESS_XREF_EventsLocationsDTO> {
    return this.eventService.addOrUpdateEventLocationStaging(eventLocationDTO);
  }

  public addExistingEventLocationsToStaging(eventLocationDTOs: ServerModels.ENESS_XREF_EventsLocationsDTO[]): Observable<ServerModels.ENESS_XREF_EventsLocationsDTO[]> {
    return this.eventService.addExistingEventLocationsToStaging(eventLocationDTOs);
  }

  public deleteEventLocation(eventLocationStagingId: string): Observable<number> {
    return this.eventService.deleteEventLocationStaging(eventLocationStagingId);
  }

  public uploadEventAttachmentsStaging(formData: FormData): Observable<ServerModels.ENESS_XREF_EventAttachmentsDTO[]> {
    return this.eventService.uploadEventAttachmentsStaging(formData);
  }
}

